import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, IconButton, Typography, Button } from '@mui/material'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import theme from 'lib/theme'
import { useServiceAreas } from 'providers/ServiceAreasProvider'
import { format, startOfISOWeek } from 'date-fns'
import { ToolbarProps, Views } from 'react-big-calendar'
import DateRangeIcon from '@mui/icons-material/DateRange'
import { ExpandMore, ExpandLess } from '@mui/icons-material'
import Calendar from 'components/Calendar/Calendar'
import { useLocale } from 'providers/LocaleProvider'

const CalendarSelectBtn = ({
  setOpen,
  open,
}: {
  setOpen: (value: boolean) => void
  open: boolean
}) => {
  const { t } = useTranslation()
  return (
    <IconButton
      aria-label={t('selectDateForSlotCalendar')}
      sx={{
        backgroundColor: 'transparent',
        '&:hover': { backgroundColor: 'transparent' },
        position: 'relative',
        marginLeft: theme.spacing(1),
      }}
      onClick={() => setOpen(!open)}>
      <DateRangeIcon
        sx={{
          height: '18px',
          position: 'absolute',
          right: '20px',
        }}
      />
      {open ? (
        <ExpandLess fontSize='small' sx={{ height: '18px' }} />
      ) : (
        <ExpandMore fontSize='small' sx={{ height: '18px' }} />
      )}
    </IconButton>
  )
}

export const CustomToolbar = ({
  onNavigate,
  view,
  onView,
  date,
}: ToolbarProps) => {
  const { t } = useTranslation()
  const { dateData, setDateData } = useServiceAreas()
  const { selectedLocale } = useLocale()
  // Locale needs to be specified bc datefns global localization setting does not work here.
  // Localizer can be used from rbc toolbar props but its format method is not documented and Typescript will complain
  const month = format(date, 'MMMM', { locale: selectedLocale }).toUpperCase()
  const year = format(dateData.date, 'yyyy')
  const week = format(dateData.date, 'w')

  const goToPrev = () => {
    onNavigate('PREV')
  }

  const goToNext = () => {
    onNavigate('NEXT')
  }
  const dateToDisplay = `${month} ${year}, ${t(
    'weekShortform'
  ).toUpperCase()} ${week}`

  return (
    <Box
      display='flex'
      alignItems='center'
      justifyContent='space-between'
      sx={{
        height: theme.spacing(3),
        padding: theme.spacing(2),
        paddingBottom: theme.spacing(3),
      }}>
      <Box display='flex' alignItems='center'>
        <Typography
          variant='body2'
          sx={{ color: theme.palette.text.secondary, fontSize: '10px' }}>
          {dateToDisplay}
        </Typography>
        <IconButton
          aria-label={t('serviceArea.goToPrev')}
          onClick={goToPrev}
          sx={{
            marginLeft: theme.spacing(2),
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}>
          <ArrowBackIosIcon
            sx={{
              fill: theme.palette.icon.main,
              fontSize: '10px',
              fontWeight: 'bold',
              '&:hover': {
                fill: theme.palette.icon.hover,
              },
            }}
          />
        </IconButton>
        <IconButton
          aria-label={t('serviceArea.goToNext')}
          onClick={goToNext}
          sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
          <ArrowForwardIosIcon
            sx={{
              fontSize: '10px',
              fontWeight: 'bold',
              fill: theme.palette.icon.main,
              '&:hover': {
                fill: theme.palette.icon.hover,
              },
            }}
          />
        </IconButton>
      </Box>
      <Box display='flex' alignItems='center'>
        <Button
          size='small'
          variant='text'
          onClick={() => onView(Views.WEEK)}
          sx={{
            marginRight: theme.spacing(0.5),
            minWidth: '20px',
            display: 'block',
            backgroundColor: 'transparent',
            height: '10px',
            padding: 0,
            '&:hover': {
              backgroundColor: 'transparent',
              cursor: view === 'week' ? 'auto' : 'pointer',
            },
          }}>
          <Typography
            variant='body2'
            sx={{
              color:
                view === 'week'
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary,
              fontSize: '10px',
              '&:hover': {
                color:
                  view === 'week'
                    ? theme.palette.primary.main
                    : theme.palette.icon.hover,
                cursor: view === 'week' ? 'auto' : 'pointer',
              },
            }}>
            {t('week')}
          </Typography>
        </Button>
        <Button
          size='small'
          variant='text'
          onClick={() => onView(Views.DAY)}
          sx={{
            minWidth: '20px',
            display: 'block',
            backgroundColor: 'transparent',
            height: '10px',
            padding: 0,
            '&:hover': {
              backgroundColor: 'transparent',
              cursor: view === 'week' ? 'auto' : 'pointer',
            },
            marginRight: '8px',
          }}>
          <Typography
            variant='body2'
            sx={{
              color:
                view === 'day'
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary,
              fontSize: '10px',
              '&:hover': {
                color:
                  view === 'day'
                    ? theme.palette.primary.main
                    : theme.palette.icon.hover,
                cursor: view === 'day' ? 'auto' : 'pointer',
              },
            }}>
            {t('day').toUpperCase()}
          </Typography>
        </Button>
        <Calendar
          selectBtn={CalendarSelectBtn}
          ariaLabel={t('serviceArea.calendar-aria')}
          value={dateData.date}
          handleChange={(value) => {
            setDateData({
              startOfWeek: startOfISOWeek(new Date(value)),
              date: new Date(value),
            })
          }}
        />
      </Box>
    </Box>
  )
}
